import { useLogin } from './hooks/useLogin';
import { LoginHeader, LoginForm } from './components';
import { loginFormSchema, type LoginFormData } from './schemas';
import SSOLogin from './components/SSOLogin';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { showToast } from '@/shared/utils';

/**
 * Login Page Component
 * Orchestrates login flow with modular components
 *
 * Components can be easily swapped or removed:
 * - LoginHeader: Logo and title
 * - LoginForm: Form inputs and submit (uses React Hook Form + Zod)
 * - SSOLogin: Single Sign-On option (uses company code from form)
 *
 * SSO Error Handling:
 * - Backend redirects to /auth?ssoError=CODE&message=TEXT on SSO failures
 * - Component checks URL params on mount and displays error toast
 * - Cleans URL after displaying error for better UX
 */
const Login: React.FC = () => {
  const { isLoading, error, handleLogin, clearError } = useLogin();
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize form validation once at parent level
  const {
    control,
    handleSubmit,
    trigger,
    getValues,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginFormSchema),
    defaultValues: {
      companyCode: '',
      userId: '',
      password: '',
      remember: false,
    },
    mode: 'onChange', // Validate on every change for immediate feedback
  });

  /**
   * Check for SSO error parameters from backend redirect
   * Backend redirects to /login?sso_error=CODE&sso_message=MSG when SSO fails
   */
  useEffect(() => {
    const ssoError = searchParams.get('sso_error');
    const ssoMessage = searchParams.get('sso_message');

    if (ssoError || ssoMessage) {
      // Display error toast
      showToast({
        type: 'error',
        title: 'SSO Login Failed',
        message: ssoMessage
          ? decodeURIComponent(ssoMessage)
          : 'SSO authentication failed',
      });

      // Clean URL params
      searchParams.delete('sso_error');
      searchParams.delete('sso_message');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  /**
   * Handle regular login form submission
   */
  const handleFormSubmit = async (values: LoginFormData) => {
    clearError();
    try {
      await handleLogin(values);
    } catch {
      // Error is handled in the hook and displayed via toast notification
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: '20px',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '500px',
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
          padding: '40px',
        }}
      >
        {/* Header Section */}
        <LoginHeader />

        {/* Form Section - Uses shared form control */}
        <LoginForm
          control={control}
          errors={errors}
          trigger={trigger}
          getValues={getValues}
          isLoading={isLoading}
          error={error}
          disabled={isLoading}
          onSubmit={handleSubmit(handleFormSubmit)}
          onClearError={clearError}
        />

        {/* SSO Login Section - Uses company code field from shared form */}
        <SSOLogin
          trigger={trigger}
          getValues={getValues}
          disabled={isLoading}
        />
      </div>
    </div>
  );
};

export default Login;
